import React, { useMemo, useRef } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SBBox, SBButton, SBIcon } from "modules/Commons";
import { useSnackbar } from "modules/Commons/SBOverlays/SBSnackbar";
import { SBNewTable } from "modules/Commons/SBDataDisplays/SBTable/SBNewTable";
import { IForecastReal } from "domain/Predictive";

import { IProductModelTimeSeriesTableProps, ITimeSeriesRow } from "../models/IProductModelTimeSeriesTableProps";
import { useProductModelTimeSeriesHeaders } from "./useProductModelTimeSeriesHeaders";

const ProductModelTimeSeriesTable: React.FC<IProductModelTimeSeriesTableProps> = ({ 
	demand, 
	benchmarkDemand,
	primaryLabel,
	benchmarkLabel
}) => {
	const { t, i18n } = useTranslation();
	const tableRef = useRef<any>();
	const snackbar = useSnackbar();

	const formatDate = (dateString: string) => {
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString();
		} catch (e) {
			return dateString;
		}
	};

	const { headers } = useProductModelTimeSeriesHeaders(primaryLabel, benchmarkLabel);

	const { rows, tableKey } = useMemo(() => {
		const allDates = Array.from(new Set([
			...Object.keys(demand || {}),
			...Object.keys(benchmarkDemand || {})
		]));
		allDates.sort();

		const mappedRows: ITimeSeriesRow[] = allDates.map(date => {
			return {
				id: date,
				date: formatDate(date),
				real: demand?.[date]?.real ?? null,
				model1: demand?.[date]?.forecast ?? null,
				model2: benchmarkDemand?.[date]?.forecast ?? null,
			};
		});

		const key = `timeseries-${primaryLabel}-${benchmarkLabel}-${allDates.length}`;

		return { rows: mappedRows, tableKey: key };
	}, [demand, benchmarkDemand, primaryLabel, benchmarkLabel]);

	if (rows.length === 0) {
		return null;
	}

	const formatForClipboard = (value: number | null) => {
		if (value === null || value === undefined) return "";
		const strValue = String(value);
		if (i18n.language && i18n.language.toLowerCase().startsWith('pt')) {
			return strValue.replace(".", ",");
		}
		return strValue;
	};

	const copyToClipboard = () => {
		const headerRow = [
			t("date"), 
			t("real"),
			...(primaryLabel ? [primaryLabel] : []),
			...(benchmarkLabel ? [benchmarkLabel] : [])
		].join('\t');

		const dataRows = rows.map(row => {
			const line = [
				row.date,
				formatForClipboard(row.real),
				...(primaryLabel ? [formatForClipboard(row.model1)] : []),
				...(benchmarkLabel ? [formatForClipboard(row.model2)] : [])
			];
			return line.join('\t');
		}).join('\n');

		const content = `${headerRow}\n${dataRows}`;

		navigator.clipboard.writeText(content).then(() => {
			snackbar.success(t("copiedToClipboard"));
		}).catch(() => {
			snackbar.error(t("copyFailed"));
		});
	};

	return (
		<SBBox mt={0} mb={2}>
			<SBNewTable
				key={tableKey}
				ref={tableRef}
				title={t("timeSeriesDetails")}
				startRows={rows}
				defaultColumns={headers}
				containerHeight={400}
				virtualize
				onClick={() => {}}
				toolbarContent={() => (
					<SBBox display="flex" alignItems="center">
						<Tooltip title={t("copy")}>
							<IconButton onClick={copyToClipboard} size="small" color="primary">
								<SBIcon icon="copy" size={18} />
							</IconButton>
						</Tooltip>
					</SBBox>
				)}
			/>
		</SBBox>
	);
};

export default ProductModelTimeSeriesTable;
